// src/contexts/useReloadOnUnrecoveredSubscription.ts
import { useEffect } from "react";
import { logToSentry } from "utils/logger";
import {
	getLastRecoveryReloadAt,
	RECOVERY_RELOAD_STORAGE_KEY,
	RECOVERY_RELOAD_STORM_WINDOW_MS,
} from "utils/recoveryReload";

const RELOAD_GRACE_MS = 15_000;

type Params = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	shouldSkip: boolean;
	fridgeId: string;
};

/**
 * Last-resort recovery for a subscription that won't revive. When the stream stays broken WHILE the
 * network is up (the zombie signature) past RELOAD_GRACE_MS — a live frame would have cleared
 * isSubscriptionBroken — reload the page (the automated version of the manual fix). Genuine offline
 * (appConnected false) is excluded; useNavigateHomeOnSustainedDisconnect owns that. An intentionally
 * skipped subscription (invalid id / UNAUTHORIZED / CONFLICT-no-sales-point) is excluded too: reloading
 * can't revive a stream we're deliberately not running (the same error recurs on boot), so a stale
 * broken flag left over the skip edge must NOT reload-loop behind the error modal —
 * useRecoverFromFridgeError owns that recovery via the hourly refetch.
 *
 * A sessionStorage timestamp RATE-LIMITS reloads to one per RELOAD_STORM_WINDOW_MS so a
 * boot-into-bad-state can't reload-loop every 15s — but it must never LOCK OUT: when a reload lands
 * back in the zombie, we DEFER the next attempt to when the window elapses rather than giving up. (The
 * old code returned without rescheduling, and since the effect deps are constant in a steady zombie it
 * never re-armed — stranding the kiosk on the error modal until a manual remote reload.)
 */
export const useReloadOnUnrecoveredSubscription = ({
	isSubscriptionBroken,
	appConnected,
	shouldSkip,
	fridgeId,
}: Params): void => {
	useEffect(() => {
		if (!(isSubscriptionBroken && appConnected) || shouldSkip) {
			return;
		}
		// `last === 0` means no prior auto-reload recorded — reload after the normal grace. Otherwise defer
		// the next reload until the window elapses (compared explicitly so a stored 0 can't skew it). A
		// steady zombie re-runs this effect on each reload (fresh page), so it keeps retrying ~once per
		// window until a live frame clears isSubscriptionBroken — at which point cleanup cancels the timer.
		const last = getLastRecoveryReloadAt();
		const stormWait = last === 0 ? 0 : Math.max(0, RECOVERY_RELOAD_STORM_WINDOW_MS - (Date.now() - last));
		if (stormWait > 0) {
			logToSentry(
				`Subscription still unrecovered after a recent reload; deferring reload, fridge: ${fridgeId}`,
				"warning",
			);
		}
		const timer = window.setTimeout(() => {
			sessionStorage.setItem(RECOVERY_RELOAD_STORAGE_KEY, String(Date.now()));
			logToSentry(`Reloading to recover an unrecovered subscription, fridge: ${fridgeId}`, "warning");
			window.location.reload();
		}, RELOAD_GRACE_MS + stormWait);
		return () => window.clearTimeout(timer);
	}, [isSubscriptionBroken, appConnected, shouldSkip, fridgeId]);
};
